import type React from 'react';
import { useMemo } from 'react';

import { useGetEvent, useGetFace } from '@vlabs/api/artemy/client';
import type { FaceId } from '@vlabs/api/artemy/model';
import { PassportCard } from '@vlabs/monokit/shared/ui/passport-card';
import type { PassportDataDTO, PassportImagesDTO } from '@vlabs/monokit/shared/ui/passport-card/PassportCard';
import { useHasPermission } from '@vlabs/permissions';
import { downloadJson } from '@vlabs/shared/utils';
import { Button } from '@vlabs/uikit/components/controls/button/Button';
import { useTranslation } from 'react-i18next';

import { PageSection } from '../../page-section';

interface FaceParentEventSectionProps {
  faceId: FaceId;
}

function FaceParentEventSection({
  faceId,
}: FaceParentEventSectionProps): React.JSX.Element {
  const { t } = useTranslation();
  const canViewEventDetails = useHasPermission('event_fs', 'read');
  const getFace = useGetFace(faceId);
  // @ts-expect-error enabled
  const getEvent = useGetEvent(getFace.data?.event_id, { target: ['meta', 'event_id'] }, {
    query: {
      enabled: !!getFace.data?.event_id,
    },
  });

  const downloadAsJsonButton = useMemo(() => {
    if (getEvent.data?.meta?.schema_name !== 'mali') return undefined;

    return (
      <Button
        kind="primary"
        onClick={() => downloadJson(`${getEvent.data.event_id}_meta`, getEvent.data.meta)}
        size="small"
        variant="outlined"
      >
        {t('face-page:meta_information.download_as_json')}
      </Button>
    );
  }, [getEvent.data, t]);

  if (!canViewEventDetails) return null;

  return (
    <PageSection title={t('face-page:meta_information.title')}>
      {!!getEvent.data?.meta && getEvent.data.meta.schema_name === 'mali' && (
        <PassportCard
          images={getEvent.data.meta.passport_saved_images as PassportImagesDTO}
          passportData={getEvent.data.meta.passport_data as PassportDataDTO}
        />
      )}
      {!!getEvent.data?.meta && getEvent.data.meta.schema_name !== 'mali' && downloadAsJsonButton}
      {!getEvent.data?.meta && <div className="NoMeta">{t('face-page:meta_information.no_meta')}</div> }
    </PageSection>
  );
}

export { FaceParentEventSection };
